/**
 * Agent id of Arachne, the context-graph neighborhood analyst. Backs the
 * hover-triggered insight panel on the finance Context Graph.
 *
 * `arachne`, NOT `arachne-v2`. The CR's `metadata.name` is the versioned
 * snapshot (`{agent_id}-v{version}`); the logical id Rain routes on is the
 * `rain.io/agent` label, and the gateway route matches
 * `…/agents/arachne/threads.*`. Same relationship as `sherlock` ↔ `sherlock-v*`.
 *
 * Workspace-scoped: `arachne.k8s.yaml` declares only `organizationId` and
 * `workspaceId`, so threads live at
 * `…/organizations/{org}/workspaces/{ws}/agents/arachne/threads/…`.
 *
 * Deliberately lighter than Sherlock: Arachne fires on a hover dwell, so she
 * runs a smaller model and holds only node-scoped Argus reads plus three
 * findings reads. Deep investigation — BDK, Jarvis, the web — is Sherlock's job.
 */
export const RAIN_ARACHNE_AGENT_ID = 'arachne';

/**
 * Arachne's tools, as the *friendly* names her CR's metadata adapters expose —
 * not the underlying gRPC methods (`argus.v1.ArgusService.GetEntity` arrives as
 * `get_entity`).
 *
 * Narrower than `ESherlockTool` on purpose. The workspace-wide reads
 * (`list_entities`, `search_entities`, `list_facts`, `list_connectors`,
 * `*_sync_job*`) are absent because a hover insight is about one node's
 * neighborhood, and those invite full-workspace scans.
 */
export enum EArachneTool {
  // Argus entity/fact graph — the web around the focused target.
  GetEntity = 'get_entity',
  TraverseEntity = 'traverse_entity',
  FindSharedNeighbors = 'find_shared_neighbors',
  GetFact = 'get_fact',
  ListFactHistory = 'list_fact_history',
  ReverseResolution = 'reverse_resolution',
  GetOntology = 'get_ontology',
  GetConnector = 'get_connector',
  // Findings service — so a known finding isn't re-reported as news.
  ListFindings = 'list_findings',
  GetFinding = 'get_finding',
  ListDetectors = 'list_detectors',
}
